import { Box, Checkbox, Heading, Stack, Text } from '@chakra-ui/react'
import { useAppendMeetingStepDataMutation } from '@gql'
import { parseChecklistItems } from '@rolebase/shared/model/checklistItems'
import {
  ChecklistTickKey,
  MeetingStepChecklistFragment,
  checklistCheckedByRole,
  checklistTickKey,
} from '@rolebase/shared/model/meeting_step'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MeetingContext } from '../contexts/MeetingContext'
import useMeetingStepRoles from '../hooks/useMeetingStepRoles'
import MeetingStepRoleFilter from './MeetingStepRoleFilter'

interface Props {
  step: MeetingStepChecklistFragment
}

// The Checklist step: one block per role of the circle and of its direct
// children, each item a box to tick (REU-04).
//
// 🔑 Ticking writes on the STEP, never on the role. Yohan's own definition: a
// checklist is "une action à faire de manière récurrente mais qui n'est pas
// encore acquise par le rôle. C'est un reminder. Quand je coche une case en
// réunion, elle ne se coche pas dans le rôle" (REU04-D4). So the role stays the
// clean template and every meeting starts blank — which is what a weekly
// checklist needs.
//
// 🔑 Each tick writes only its own key (`checked:<roleId>:<index>`), merged
// by `_append` (#46, 25/09/2026): two people ticking at the same instant, even
// on the same role, no longer erase each other's box. The role filter is
// written the same way, so it cannot erase a tick either. Past meetings keep
// their former `checked` object, read by checklistCheckedByRole.
//
// ⚠️ The items are rendered here rather than by the editor in read-only mode:
// the field is Markdown, so a pure module cuts it into lines and each line
// becomes a checkbox. The trade-off is stated in the plan — rich formatting
// inside a checklist line (bold, links) is dropped, only the text remains.
export default function MeetingStepContentChecklist({ step }: Props) {
  const { t } = useTranslation()
  const { meeting, editable } = useContext(MeetingContext)!
  const [appendMeetingStepData] = useAppendMeetingStepDataMutation()

  const filterRolesIds = step.data.filterRolesIds
  const { all: allRoles, filtered: roles } = useMeetingStepRoles(
    'checklist',
    filterRolesIds
  )

  // Only the given keys are sent, merged into the stored data
  const appendData = useCallback(
    (data: Partial<MeetingStepChecklistFragment['data']>) =>
      appendMeetingStepData({ variables: { id: step.id, data } }),
    [step.id, appendMeetingStepData]
  )

  // The tick, held here until the server's version catches up (BUG-02).
  //
  // 🔑 This screen redraws ONLY when the subscription echoes the step back,
  // which measured 1–2 s in a meeting. An Apollo `optimisticResponse` cannot
  // shorten that: `useSubscription` reads its data straight from
  // `client.subscribe()`, never through `cache.diff`/`watchQuery`, so writing
  // to the normalised cache changes nothing on screen. Tried, and dropped on
  // 13/08/2026 — don't reach for it again.
  const [optimisticTicks, setOptimisticTicks] = useState<
    Partial<Record<ChecklistTickKey, boolean>>
  >({})

  // The server's answer always wins, as soon as it lands.
  useEffect(() => setOptimisticTicks({}), [step.data])

  const checkedByRole = checklistCheckedByRole({
    ...step.data,
    ...optimisticTicks,
  })

  const handleFilterChange = useCallback(
    (rolesIds: string[]) => appendData({ filterRolesIds: rolesIds }),
    [appendData]
  )

  const handleToggle = useCallback(
    (roleId: string, index: number, checked: boolean) => {
      const key = checklistTickKey(roleId, index)
      setOptimisticTicks((ticks) => ({ ...ticks, [key]: checked }))
      appendData({ [key]: checked })
    },
    [appendData]
  )

  if (!meeting) return null

  return (
    <Box>
      <MeetingStepRoleFilter
        roles={allRoles.map((r) => r.role)}
        selectedIds={filterRolesIds ?? []}
        editable={editable}
        onChange={handleFilterChange}
      />

      {roles.length === 0 ? (
        <Text color="gray.500" textAlign="center" py={6}>
          {filterRolesIds?.length
            ? t('MeetingStepContentChecklist.emptyFiltered')
            : t('MeetingStepContentChecklist.empty')}
        </Text>
      ) : (
        roles.map(({ role }) => {
          const checked = checkedByRole[role.id] ?? []
          return (
            <Box key={role.id} mb={6}>
              <Heading as="h3" size="sm" mb={2}>
                {role.name}
              </Heading>
              <Stack spacing={1}>
                {parseChecklistItems(role.checklist).map((item) => (
                  <Checkbox
                    key={item.index}
                    isChecked={checked.includes(item.index)}
                    // Disabled rather than hidden for a read-only guest: the
                    // state of the round stays visible, only changing it is
                    // out of reach.
                    isDisabled={!editable}
                    onChange={(e) =>
                      handleToggle(role.id, item.index, e.target.checked)
                    }
                  >
                    {item.text}
                  </Checkbox>
                ))}
              </Stack>
            </Box>
          )
        })
      )}
    </Box>
  )
}
